// **Canned stage content for the demo's staged world** (`demo-presentation-shell`,
// `docs/PRESENTATION.md`). Each `DemoStageSpec` here is the world a `demo_configure` stage cue
// establishes — a presenter-content decision this change owns (the script names *what* happens on
// stage, not the exact bee positions or B++ text), kept in one small module so a content tweak
// before the show never touches the cue list or the sequencer.
//
// **There are two stages, not four** (E2E round-3 findings H / V4-builder). The script is one
// continuous world from the fade-in of chapter 1 until the explicit jump — "If I keep applying this
// concept, I end up with something like this" — and it was being staged as one world *per chapter*:
// every chapter cue re-ran `demo_configure`, so the enemy raid's damage was thrown away before the
// chapter-3 Beekeeper beat that narrates it ("my hive is a bit busted now") could pay off, and
// staging that damage was inexpressible — a stage describes cells, never their hp.
//
// So {@link CONTINUOUS_STAGE} is configured **once**, at chapter 1, and chapters 1, 1b, 2 and
// chapter 3's Beekeeper beat all mutate it: the spawns, the enemy raid that razes real cells, the
// presenter's typed harvester program, and the Beekeeper's builder document. Only the full-hive beat
// reconfigures, because the script says it is a different world; `demo-run`'s own gate ("a queen, 80
// staged workers, 120 cells, a stored pool sized to cover the brood draw" ⇒ ≥ 100 living bees after
// one `fast_forward(4 500)`) is measured over exactly the stage {@link CHAPTER3_FULL_HIVE_STAGE}
// mirrors. `cues.ts`'s `configureAnchorIndex` resolves any cursor to the configure its world stands
// on, so the chapter-jump and stage-reset failsafes replay the whole chain — spawns, raid and all —
// from that one configure.
//
// **Every non-empty program below carries its own `type <name> buffs <buff>` header**, because
// that is the grammar C-1 compiles a staged program against (`waggle-round`'s `compile_source` →
// `compile_against`) — task 13.3 found every program here headerless and refused `compile_failed`
// by the real module. The header's type name must be the colony's own registered name, and the
// header's buff must be that type's buff: the `buff` field beside the source is what the *sim*
// gives the type, the header is what the *compiler* checks the program against, and a stage where
// the two disagree is a stage that lies about what the audience is watching.
//
// `idle` is a **reserved keyword** and cannot name a state (it is an action), which is the other
// thing the real compiler said and the fake did not.

import type { DemoStageSpec } from './stage-driver';
import fullHiveGrovePoints from '../../../../fixtures/demo-c3-groves.json';
import { stageFrameOf } from './stage-frame';

/** A harmless placeholder program: idles forever. Chapter 1's bees are player-controlled by
 * right-click (design **DD5**), so their starting program never runs a decision that matters —
 * and chapter 2 opens on it, because "the harvester type has no behaviour yet" is what the beat
 * needs and an idling program is that observably (the empty-source form C-1 also accepts was a
 * property of the chapter-2 stage this continuity model no longer has). */
const IDLE_PROGRAM = 'type harvester buffs capacity\n\nstate resting:\n  do idle\n';

/**
 * **The raiding enemy's program** — `raze`, which attacks the nearest enemy *cell*.
 *
 * It used to be `fight` with a `cell_damaged`-guarded `raze` state, and that could never damage
 * the player's hive at all (E2E round-3 finding H): `cell_damaged` is a **colony-scope** predicate
 * about the *sensing* bee's own colony, so the guard asked whether the raider's own comb was hurt,
 * and `fight` attacks bees, not cells. The script says the enemy "starts attacking the hive", so
 * the program says `raze` and nothing else — the damage chapter 3 narrates is damage the audience
 * actually watched being done.
 *
 * The type is **range**-buffed rather than strength-buffed: `raze` moves the bee nowhere (it hits
 * whatever live enemy cell is inside its interaction range), so reach is what decides whether one
 * raider standing in the comb can work more than the single cell it landed on.
 */
export const ENEMY_AGGRESSIVE_PROGRAM = 'type raider buffs range\n\nstate raid:\n  do raze\n';

/**
 * The stores a *presented* chapter opens with.
 *
 * DESIGN §14's 100 is a match's starting pool, and a match starts earning immediately; a chapter
 * does not. Chapter 1 idles under the presenter's finger for as long as three real LLM instruct
 * turns take, while the queen keeps laying and the brood keep drawing — the E2E drive measured the
 * counter at 0 for most of chapters 1–3, which turns "watch the nectar come in" into watching a
 * zero. This is a **stage prop**, not a balance value: the number the audience reads has to survive
 * the presenter talking over it.
 *
 * **And it must leave headroom under the colony's own ceiling** (E2E finding N2). A pool cannot
 * exceed `cells × hive.per_cell_capacity` — 50 nectar a cell at the demo preset — so a pool staged
 * *at* its ceiling makes the point-and-click chapter's payoff (a bee unloading) physically unable to
 * move the counter. 1 200 against {@link PRESENTED_CELLS} leaves room for dozens of deposits on a
 * number that still reads big from the back of a room, **and** absorbs the ceiling drop the raid
 * causes: every cell the enemy destroys takes 50 off the cap and spills its share of the pool.
 */
const PRESENTED_POOL = 1200;

/**
 * The comb the presented chapters open with — larger than DESIGN §14's 30 for the same reason
 * {@link PRESENTED_POOL} is what it is. The raid is now real (`ENEMY_AGGRESSIVE_PROGRAM`), so the
 * hive it chews through has to be big enough that "a bit busted" leaves a working colony with
 * headroom above its stores rather than a ceiling underneath them.
 */
const PRESENTED_CELLS = 40;

/**
 * **The chapters a presenter talks over run with the queen's laying switched off** (E2E finding
 * N2, `waggle-balance`'s `Balance::demo_narration`).
 *
 * The `demo` preset is dimensioned for chapter 3's full hive — an egg a second, brood maturing in
 * fifteen — because that beat has to grow to hundreds inside a 300 s fast-forward. Chapters 1 and
 * 2 run at conversational speed under the same clock, and the E2E drive measured what that costs:
 * **49 bees under a counter narrated as "one bee"**, 150 by chapter 2, and the 1 500-nectar pantry
 * above drained to zero within three minutes by a brood pipeline nothing in the script mentions —
 * so the whole "watch her carry it home" payoff played out over a counter reading 0.
 *
 * With no laying, chapter 1 holds at exactly the bees its `spawn` cues put on the field (one, then
 * two, then ten) and its pool moves only when a bee unloads into it. The full-hive stage keeps the
 * fast rates: it is the one beat that wants them.
 */
const NARRATED: Pick<DemoStageSpec, 'balance'> = { balance: 'narration' };

/**
 * **The enemy hive's world position** (presentation-polish item C-1, 2026-08-11: "the second hive
 * on the right the audience was never meant to see"). C-1 stages an implicit queen per colony, so
 * this hive is also staging an enemy queen the audience can see wander around it — the colony
 * cannot simply be dropped, because {@link ENEMY_RAID_POSITION}'s raid spawn needs an enemy colony
 * to exist at all.
 *
 * **Moving this number was never the fix, and the record should say so.** A first pass at the
 * complaint moved the hive from `500` to `480` on the theory that it was sitting just inside what
 * the camera showed. It was not: a probe against the real {@link stageFrameOf}/`defaultStageCamera`
 * put the old `500` at screen x ≈ 3323 px on a 1920 x 1080 default camera — already far outside the
 * viewport, and `480` is 20 u *closer*. What the audience was actually looking at was the enemy's
 * **comb**: `cells` is optional on a colony spec and `stage-json.ts` defaults it to DESIGN §14's
 * 30, so the enemy colony had been staging a full 30-cell hive nobody ever asked it for. `cells: 0`
 * below is the actual fix; see the note there.
 *
 * `480` is kept only because it is tidier than `500` — it leaves the colony `32` u inside the
 * `demo`/`narration` presets' own field half-extent (`field_size_u` 1024 / 2 = 512,
 * `waggle-balance/presets/demo.toml`) rather than sitting on the boundary of the world it is staged
 * in. Nothing observable depends on it, and `DemoStage.test.tsx`'s camera assertion passed before
 * the move as well as after, so do not read that test as evidence for this number.
 */
const ENEMY_HIVE_X = 480;

/**
 * **The one world chapters 1, 1b, 2 and chapter 3's Beekeeper beat all run on** (see the module
 * header). Configured once, at chapter 1's own cue, and never re-configured until the full-hive
 * beat — so the raid's damage is still there when the script says "my hive is a bit busted now".
 *
 * The **enemy colony stages no worker at all**: the raid is a `spawn` cue that puts one raider down
 * inside the player's comb, which is both "a scripted enemy bee comes in" as written and the only
 * way `raze` (which moves the bee nowhere) can reach a cell. A raider staged from tick 0 would have
 * to stand in the player's hive through the whole of chapter 1, spoiling the beat it exists for.
 */
export const CONTINUOUS_STAGE: DemoStageSpec = {
  ...NARRATED,
  colonies: [
    {
      name: 'Player',
      birthType: 'harvester',
      hive: { x: 0, y: 0 },
      types: [{ name: 'harvester', buff: 'capacity', program: IDLE_PROGRAM }],
      // Well clear of the comb (2026-08-09 request): chapter 1 opens with the camera centred on
      // this one bee, zoomed so the hive is off-screen — a bee at the old (20, 0) sat *inside*
      // the comb, which made that framing impossible. From here the near flower at (120, 60) is
      // in the opening shot and the hive is a pan/zoom away.
      bees: [{ type: 'harvester', position: { x: 240, y: 120 } }],
      pool: PRESENTED_POOL,
      cells: PRESENTED_CELLS,
    },
    {
      name: 'Enemy',
      birthType: 'raider',
      hive: { x: ENEMY_HIVE_X, y: 0 },
      types: [{ name: 'raider', buff: 'range', program: ENEMY_AGGRESSIVE_PROGRAM }],
      bees: [],
      // **The pool stays generous even though there is no comb to hold it, and that is deliberate.**
      // A queen eats from stores like any bee and famine is an elimination path (DESIGN §3), and her
      // death takes the whole colony — including the raider — with her. This one world runs from
      // chapter 1 through chapter 3's Beekeeper beat, which at the demo's new 2x default is minutes
      // of world time, so an enemy queen starved out mid-show would silently delete the raid beat
      // she exists to enable. A pool above `cells x per_cell_capacity` is inert rather than
      // ill-formed: capacity is computed on demand where the pool is *mutated* (`stores.rs`'s
      // `pool_capacity`), and nothing ever unloads into this colony.
      pool: PRESENTED_POOL,
      // **No comb — this is the "second hive on the right nobody was meant to see"**
      // (presentation-polish item C-1, 2026-08-11). `cells` is optional and `stage-json.ts` defaults
      // it to DESIGN §14's 30, so omitting it staged the enemy a full 30-cell hive: a second comb on
      // the field, at hive scale, for a colony whose only job is to own one raider's program. Zero
      // is the whole fix — the hive *point* was never the visible thing (see {@link ENEMY_HIVE_X}).
      //
      // Zero is safe, not merely untested: staging materialises `cells` cells in slot order, so this
      // is a zero-iteration loop; the spill share divides by the live cell count but guards on
      // `> 0` (`combat.rs`), and the snapshot's own `pool / cell_count` runs only while iterating
      // cells that exist. What remains visible of this colony is its implicit queen — one sprite,
      // 480 u out, off the default camera entirely.
      cells: 0,
    },
  ],
  // **Four hand-placed clusters, not a lattice** (presentation-polish item C-2, 2026-08-11):
  // ~4-5 patches each, grove-*shaped* by hand rather than generated — a real grove generator
  // risks both properties this stage needs and cannot re-derive on its own. `waggle-round`'s
  // stage validator refuses any two patch centres within `2 x bee.arrival_tolerance_u` of one
  // another (`Reason::PatchesTooClose`; the `demo`/`narration` presets' `arrival_tolerance_u` is
  // `1.0`, so the rule is "more than 2 u apart"), and the two E2E-measured beats below need their
  // patches at roughly their *original* distance from the hive, not wherever a generator's seed
  // happens to land them.
  //
  // **Two clusters preserve the far pair, two the near pair** (E2E findings P6 and H — this
  // module's original two-pair design, `chapters.ts`'s own prior header). Chapter 1 wants the far
  // cluster — the audience watches the bee it clicked fly there — and chapter 2 wants the near
  // cluster: its program opens with `move_to(patch)` against an *empty* `patch`, which falls back
  // to wandering until a bee happens to sense a flower, and at the far cluster's ~134 u against a
  // 40 u sense radius that was measured at upwards of half a minute of dead air after the
  // presenter pressed Run. Every cluster keeps one patch at its predecessor's exact anchor point,
  // so neither measured distance moves; every patch in every cluster is tens of units from its
  // neighbours, comfortably clear of the 2 u `PatchesTooClose` floor.
  patches: [
    // Far cluster, NE — anchor (120, 60), ~134 u from the hive (chapter 1's watch-it-fly beat).
    { position: { x: 120, y: 60 } },
    { position: { x: 140, y: 70 } },
    { position: { x: 108, y: 44 } },
    { position: { x: 126, y: 82 } },
    { position: { x: 96, y: 66 } },
    // Far cluster, SW — mirrored: the same distance, the opposite side of the field.
    { position: { x: -120, y: -60 } },
    { position: { x: -140, y: -70 } },
    { position: { x: -108, y: -44 } },
    { position: { x: -126, y: -82 } },
    { position: { x: -96, y: -66 } },
    // Near cluster, SE — anchor (70, -35), ~78 u from the hive (chapter 2's no-dead-air beat).
    { position: { x: 70, y: -35 } },
    { position: { x: 88, y: -42 } },
    { position: { x: 56, y: -24 } },
    { position: { x: 80, y: -14 } },
    // Near cluster, NW — mirrored.
    { position: { x: -70, y: 35 } },
    { position: { x: -88, y: 42 } },
    { position: { x: -56, y: 24 } },
    { position: { x: -80, y: 14 } },
  ],
};

/** How far beyond the camera's own edge the raider enters, in world units — enough that it is
 * unambiguously off screen rather than clipped at the boundary. At `bee.speed_u_per_s` 20 and the
 * demo's 2x default clock, 40 u of overshoot is ~1 s of real time on top of the crossing itself. */
const ENEMY_RAID_ENTRY_MARGIN_U = 40;

/**
 * Where the raid's one enemy bee **enters the field** (2026-08-09 request: it "should fly in from
 * the side rather than appear right in the middle of the hive") — off toward the enemy hive's
 * side of the field, far enough out to read as an arrival, close enough that the flight is
 * seconds, not a scene.
 *
 * **Derived from the stage frame, not written down** (presentation-polish Wave 2, 2026-08-11).
 * {@link stageFrameOf} already computes the world rectangle the default camera frames, so "just
 * outside it" is the only statement of "off screen" this file needs, and it follows the patches
 * automatically — which matters, because the hand-placed clusters above widened that rectangle and
 * left the old hard-coded `(150, -60)` sitting *inside* it. An entry point inside the frame is not a
 * small error: the beat is the audience watching the raider arrive, and a spawn in shot fades one
 * in mid-frame instead.
 *
 * Derived from the **frame**, deliberately not from the live camera: the presenter pans and zooms
 * during chapter 1, and `demo.worker.ts:124` requires a rehearsal and the show to agree, so the
 * spawn point must not depend on presenter gestures. It is static data computed from static data.
 *
 * `raze` still issues no movement, so the spawn op pairs this entry point with
 * {@link ENEMY_RAID_TARGET}: a `move_to` override carries the raider to the comb's rim, arrival
 * releases it to its program, and `raze` then works the nearest cell — the rim first, outside-in.
 */
export const ENEMY_RAID_POSITION: { readonly x: number; readonly y: number } = (() => {
  const frame = stageFrameOf(CONTINUOUS_STAGE);
  // **Measured from the camera's centre, which in chapter 1 is the staged bee — not the frame's
  // centre.** A first version of this derivation used `frame.centre`, and it was wrong for the one
  // chapter that fires the raid: `defaultStageCamera` keeps the frame-derived *scale* but re-centres
  // on `focus`, and `DemoApp` passes chapter 1's focus as this very bee (the opening shot is centred
  // on it, zoomed so the comb is off-screen). Frame centre (0, 0) plus `halfX` put the spawn at
  // x ≈ 235, comfortably *inside* a viewport running from x ≈ 45 to x ≈ 435 — a raider fading in
  // on screen, which is the exact defect this derivation exists to prevent.
  //
  // The camera's own half-width is `frame.halfX` whenever width binds the scale, which it does for
  // every viewport up to ~2:1 — including the 1920 x 1080 the whole deck is laid out for. So
  // "focus + halfX + margin" clears the shot, and it also clears the *unfocused* framing chapters 2
  // and 3 use (centred on (0, 0), so x ≤ halfX), which matters because a chapter jump replays this
  // spawn from the configure anchor with no focus set.
  //
  // **What this costs, stated rather than discovered on stage.** The point lands at x ≈ 475, and the
  // camera actually up at this beat is the comb-centred one (the presenter is watching their hive be
  // razed), whose right edge is x ≈ 195. So at `bee.speed_u_per_s` 20 under the 2x default the
  // raider is out of shot for ~7 s of real time before it enters the frame, then takes ~4 s more to
  // reach the rim. That is a beat the presenter narrates over ("a scripted enemy bee comes in"), and
  // it is the price of the arrival being an arrival: a nearer spawn is a bee that fades in on screen.
  const focus = CONTINUOUS_STAGE.colonies[0]?.bees[0]?.position ?? { x: 0, y: 0 };
  return {
    // The enemy hive's side of the field (+x), so the arrival comes from where the enemy lives
    // rather than from an arbitrary edge. `y` stays on the camera's own centre line: an off-axis
    // entry read as a diagonal drift across the comb rather than an approach to it.
    x: focus.x + frame.halfX + ENEMY_RAID_ENTRY_MARGIN_U,
    y: focus.y,
  };
})();

/** Where the fly-in lands: just outside the comb's outermost ring (40 cells reach ~30 u at the
 * demo's 10 u spacing), inside the range-buffed raider's 10 u reach of a rim cell. */
export const ENEMY_RAID_TARGET = { x: 34, y: 0 } as const;

/**
 * Chapter 3's harvester: a working forage loop, because this stage's whole job is to *look* like
 * a hive that works. Deliberately the shape DESIGN §5's syntax sketch states, minus the enemy
 * clauses this chapter has no enemy for.
 *
 * **Exported because it is also chapter 2's script**, and the presenter's `Cmd`/`Ctrl`+`Shift`+`C`
 * recovery chord copies it (`DemoApp.tsx`). The beat manifest's
 * `stage-chapter2-run-harvester-program` names exactly this program — "the seek_flowers/gather/
 * deliver/unload harvester program" — which chapter 2's own stage cannot hold, because that chapter
 * opens on {@link IDLE_PROGRAM} by design ("the harvester type has no behaviour yet"). The chord
 * used to reach it as `CHAPTER3_FULL_HIVE_STAGE.colonies[0].types.find(name === 'harvester')`; that
 * resolved to the right text but bound the presenter's recovery to chapter 3's *roster*, so
 * re-pointing that type at a `waggle-fixtures` preset source — which the five-preset registration
 * below came close to doing — would have silently changed what the chord hands the presenter, with
 * no test failing. One named constant, two readers.
 */
export const HARVESTER_PROGRAM = `type harvester buffs capacity

state seek_flowers:
  do move_to(patch)
  when sees_flowers -> gather

state gather:
  remember patch = here
  do collect_nectar
  when nectar_full -> deliver
  when patch_empty -> seek_flowers

state deliver:
  do return_to_hive
  when at_hive -> unload

state unload:
  do drop_nectar
  when nectar == 0 -> seek_flowers
`;

/** Chapter 3's nurse: feeding is what makes the lay rate more than its floor, which is what makes
 * "hundreds of bees" arrive inside one fast-forward rather than eventually. */
const NURSE_PROGRAM = `type nurse buffs food_consumption

state feeding:
  do feed_larvae
`;

/** Chapter 3's builder — the canonical `waggle-fixtures` preset shape (`preset.rs`'s
 * `building`-buffed source), registered so the type sheet's builder chip shows a real program
 * rather than a stand-in. Neither state is named `idle` — a reserved action keyword, not a legal
 * state identifier. */
const BUILDER_PROGRAM = `type builder buffs building

state expand:
  do build
  when cell_damaged -> mend
  when hive_nectar < 10 -> waiting

state waiting:
  do idle
  when cell_damaged -> mend
  when hive_nectar >= 10 -> expand

state mend:
  do repair
  when not cell_damaged -> expand
`;

/** Chapter 3's scout — the canonical `waggle-fixtures` preset shape, `speed`-buffed. */
const SCOUT_PROGRAM = `type scout buffs speed

state search:
  do wander
  when sees_flowers and sees_family -> found
  when sees_flowers -> regroup

state found:
  do dance(here, "flowers")
  when not sees_family -> regroup
  when not sees_flowers -> search

state regroup:
  do return_to_hive
  when sees_flowers and sees_family -> found
  when at_hive -> search
`;

/** Chapter 3's warrior — the canonical `waggle-fixtures` preset shape, `strength`-buffed. */
const WARRIOR_PROGRAM = `type warrior buffs strength

state guard:
  do return_to_hive
  when health < 30 -> recover
  when enemy_within(20) -> engage

state engage:
  do fight
  when health < 30 -> recover
  when distance_to_hive > 60 -> guard
  when not sees_enemy -> guard

state recover:
  do return_to_hive
  when health >= 30 -> guard
`;

/**
 * **The pitch and jitter chapter 3's lattice widens with** (presentation-polish item C-4,
 * 2026-08-11): from a tight ±56 u to ≈±150 u, so "hundreds of bees" reads as a colony rather than
 * a rank-and-file grid. The jitter is **integer arithmetic only, no float transcendentals** — two
 * small multiplicative hashes of the bee's own index, taken mod 11 and re-centred to `[-5, 5]` u,
 * one multiplier per axis so the two offsets don't move together (`demo.worker.ts:124`'s
 * no-`Math.random` rule generalises here to "no divergently-computed float", since a rehearsal and
 * the show — and now the Rust stage and the client stage — must scatter identically).
 * `waggle-round`'s `demo_testkit::demo_stage_full_hive` computes the exact same formula in the
 * exact same order; `fixtures/demo-c3-lattice.json` pins the resulting 80 positions as a
 * cross-language golden so a silent divergence fails a test rather than only a comment.
 */
const FULL_HIVE_PITCH_U = 36;

/** The x-axis jitter for staged bee `i`: `[-5, 5]` u, deterministic. Mirrored exactly by
 * `waggle-round`'s `full_hive_jitter_x`. */
function fullHiveJitterX(i: number): number {
  return ((i * 41 + 7) % 11) - 5;
}

/** The y-axis jitter for staged bee `i` — a different multiplier from {@link fullHiveJitterX} so
 * the two axes don't move together. Mirrored exactly by `waggle-round`'s `full_hive_jitter_y`. */
function fullHiveJitterY(i: number): number {
  return ((i * 67 + 13) % 11) - 5;
}

const FULL_HIVE_ROSTER = [
  ['harvester', 49],
  ['nurse', 15],
  ['builder', 10],
  ['scout', 10],
  ['warrior', 15],
] as const;

/** A 9 × 11 lattice at {@link FULL_HIVE_PITCH_U} pitch centred on the hive: 99 workers plus the
 * separately staged queen make the requested 100-bee colony. The roster is the standard
 * harvesting-heavy assortment, with every role visibly represented.
 * Geometry, alternation and count are copied from `waggle-round`'s
 * `demo_testkit::demo_stage_full_hive`, which is the stage the ≥ 100-living-bees gate is measured
 * over; a stage that differs from the measured one is a stage with no gate. Exported so
 * `fixtures/demo-c3-lattice.json`'s client-side golden test can assert this exact geometry. */
export const FULL_HIVE_BEES = FULL_HIVE_ROSTER.flatMap(([type, count]) =>
  Array.from({ length: count }, () => type),
).map((type, i) => ({
  type,
  position: {
    x: ((i % 11) - 5) * FULL_HIVE_PITCH_U + fullHiveJitterX(i),
    y: (Math.floor(i / 11) - 4) * FULL_HIVE_PITCH_U + fullHiveJitterY(i),
  },
}));

/** Ten deterministic grove centres around the map, sixteen flower patches apiece. The hash-like
 * offsets give the same irregular silhouette on every rehearsal without using `Math.random`. */
export const FULL_HIVE_PATCHES = fullHiveGrovePoints.map((position) => ({ position }));

/**
 * **The chapter-3 full-hive stage** — the script's "If I keep applying this concept, I end up with
 * something like this". A queen (C-1 stages one per colony), 99 staged workers, 120 cells and a
 * stored pool of 4 000, with 160 Rust-generated grove flowers: `demo_stage_full_hive`'s
 * numbers exactly, so the shipped `fast_forward(4 500)` that follows lands where `demo-run`'s
 * scenario says it does.
 *
 * **All five presets are registered** (presentation-polish item C-3, 2026-08-11), so the type
 * sheet shows the real game rather than a two-type stand-in — matching `demo_stage_full_hive_spec`
 * on the Rust side, which already registers all five via `preset_stage_types()`. Every program
 * carries its own `type <name> buffs <buff>` header naming the type's actual
 * `waggle-fixtures`-preset buff (`preset.rs`'s `buffed_characteristic`) — a header that disagreed
 * with the sim's own buff would be, this module's own header says, "a stage that lies about what
 * the audience is watching". **Spawned composition stays harvester/nurse-weighted**
 * ({@link FULL_HIVE_BEES} alternates only those two) to protect the ≥ 100-living-bees gate.
 */
export const CHAPTER3_FULL_HIVE_STAGE: DemoStageSpec = {
  colonies: [
    {
      name: 'Player',
      birthType: 'harvester',
      hive: { x: 0, y: 0 },
      types: [
        { name: 'harvester', buff: 'capacity', program: HARVESTER_PROGRAM },
        { name: 'nurse', buff: 'food_consumption', program: NURSE_PROGRAM },
        { name: 'builder', buff: 'building', program: BUILDER_PROGRAM },
        { name: 'scout', buff: 'speed', program: SCOUT_PROGRAM },
        { name: 'warrior', buff: 'strength', program: WARRIOR_PROGRAM },
      ],
      bees: FULL_HIVE_BEES,
      pool: 4000,
      cells: 120,
    },
  ],
  patches: FULL_HIVE_PATCHES,
};
